import {
  assignTableAPIs,
  type CellData,
  type ColumnDef,
  type RowData,
  type Table_Internal,
  type TableFeature,
  type TableFeatures,
} from '@tanstack/react-table';

import type { DataTableGanttChartFeatureConstructors } from './gantt-chart-types.js';
import type { SelectionState } from '../../../../charts/core/components/annotations/types/state.js';
import type {
  MaxScaleBoundary,
  MinScaleBoundary,
} from '../../../../charts/core/types/min-max-config.js';
import type { MinMax } from '../../../../charts/core/types/min-max.js';
import {
  resolveMinMax,
  resolveTimeMinMax,
} from '../../../../charts/core/utils/get-min-max-for-scale.js';
import type {
  GanttAnnotationsHeaderConfig,
  GanttAnnotationsTrackConfig,
  GanttSegmentData,
} from '../../../../charts/gantt-chart/types/gantt-chart.js';
import {
  isAccessorFnColumnDef,
  isAccessorKeyColumnDef,
  isDisplayColumnDef,
} from '../../hooks/useTable/datatable-typeguards.js';
import { isDatatableGanttColumnConfig } from '../ColumnTypes/ColumnTypes.js';

// Typeguard for gantt chart data
function isDataTableGanttData(obj: unknown): obj is GanttSegmentData {
  return typeof obj === 'object' && obj !== null && 'start' in obj;
}

/**
 * Retrieves data for a specific column from a row entry.
 *
 * @param row - data row
 * @param columnDef - composed column def (useColumns)
 * @returns column data
 */
function getDataFromRow<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData = CellData,
>(row: TData, columnDef: ColumnDef<TFeatures, TData, TValue>) {
  if (isAccessorKeyColumnDef<TFeatures, TData, TValue>(columnDef)) {
    return (row as Record<string | number | symbol, unknown>)[
      columnDef.accessorKey
    ];
  }
  if (isAccessorFnColumnDef<TFeatures, TData, TValue>(columnDef)) {
    return columnDef.accessorFn(row, 0);
  }
}

/**
 * Adjusts the min/max values based on the annotations tracks.
 * If an annotation's start or end value is outside the current min/max, it used as the new min/max.
 *
 * @param tracks - annotations tracks
 * @param min - current min value
 * @param max - current max value
 * @returns adjusted min/max values
 */
const adjustMinMaxWithAnnotationsHeader = (
  tracks: GanttAnnotationsTrackConfig[],
  min: number,
  max: number,
): { min: number; max: number } => {
  // get annotations min/max if annotationsHeader is defined
  tracks.forEach((track) => {
    track.markers.forEach((marker) => {
      const start = marker.start;
      // if end is undefined, we use the start value also for the max calculation
      const end = marker.end ?? marker.start;

      const startValue = start instanceof Date ? start.valueOf() : start;
      const endValue = end instanceof Date ? end.valueOf() : end;

      min = Math.min(min, startValue);
      max = Math.max(max, endValue);
    });
  });

  return { min, max };
};

/**
 * Gets the overall min and max values in a gantt column.
 *
 * @param data - table data
 * @param columns - column definition from table options
 * @param getSubRows - function used to access subRows for a given row
 * @returns record of min/max value per column
 * @internal
 */
export function getMinMaxForGanttColumns<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData = CellData,
>(
  data: readonly TData[],
  columns: readonly ColumnDef<TFeatures, TData, TValue>[],
  getSubRows:
    | ((originalRow: TData, index: number) => readonly TData[] | undefined)
    | undefined,
): Record<string, MinMax> {
  const minMaxArray = columns
    .filter(
      (columnDef) =>
        isDisplayColumnDef(columnDef) && columnDef.columnType === 'gantt',
    )
    .map((columnDef) => {
      const config = columnDef.meta?.config;

      let configMin: MinScaleBoundary = 'data-min';
      let configMax: MaxScaleBoundary = 'data-max';
      let xAxisType: 'numerical' | 'time' | undefined = 'numerical';
      let annotationsHeader: GanttAnnotationsHeaderConfig | undefined;

      if (isDatatableGanttColumnConfig(config)) {
        configMin = config.min ?? configMin;
        configMax = config.max ?? configMax;
        xAxisType = config.xAxisType ?? xAxisType;
        annotationsHeader = config.annotationsHeader;
      }

      let ganttMin = Number.POSITIVE_INFINITY;
      let ganttMax = Number.NEGATIVE_INFINITY;

      /**
       * Check gantt start/end values in a row to find the overall min/max values.
       * @param row - data row
       */
      const findGanttMinMax = (row: TData) => {
        const ganttData = getDataFromRow(row, columnDef);

        const ganttSegments = Array.isArray(ganttData)
          ? ganttData
          : [ganttData];

        ganttSegments.forEach((segment) => {
          if (isDataTableGanttData(segment)) {
            const { start, end } = segment;
            ganttMin = Math.min(ganttMin, start);
            ganttMax = Math.max(ganttMax, end ?? start);
          }
        });

        const subRows = getSubRows?.(row, 0);

        // recursively traverse sub-rows
        subRows?.forEach((subRow) => findGanttMinMax(subRow));
      };

      for (const row of data) {
        findGanttMinMax(row);
      }

      if (annotationsHeader) {
        ({ min: ganttMin, max: ganttMax } = adjustMinMaxWithAnnotationsHeader(
          annotationsHeader.tracks,
          ganttMin,
          ganttMax,
        ));
      }

      return [
        columnDef.id,
        xAxisType === 'numerical'
          ? resolveMinMax(
              { min: ganttMin, max: ganttMax },
              { min: configMin, max: configMax },
            )
          : resolveTimeMinMax(
              { min: ganttMin, max: ganttMax },
              { min: configMin, max: configMax },
            ),
      ];
    });

  return Object.fromEntries(minMaxArray);
}

/**
 * Updates the selection state for gantt annotations in the table's internal state.
 * @internal
 */
export function table_setGanttAnnotationsSelection<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  table: Table_Internal<TFeatures, TData>,
  columnId: string,
  selection: Partial<SelectionState>,
) {
  table.baseAtoms.ganttAnnotationsSelection.set((old) => ({
    ...old,
    [columnId]: {
      ...old?.[columnId],
      ...selection,
    },
  }));
}

/**
 * Constructs the gantt chart feature for the table,
 * including API implementations and prototype assignments.
 * @internal
 */
export function constructGanttChartFeature(): TableFeature<DataTableGanttChartFeatureConstructors> {
  return {
    getInitialState: (initialState) => {
      return {
        ganttMinMaxValues: {},
        ganttAnnotationsSelection: {},
        ...initialState,
      };
    },
    constructTableAPIs: (table) => {
      assignTableAPIs('DataTableGanttChartFeature', table, {
        table_setGanttAnnotationsSelection: {
          fn: (columnId: string, selection: Partial<SelectionState>) =>
            table_setGanttAnnotationsSelection(table, columnId, selection),
        },
      });
    },
  };
}

/**
 * Additional feature implementation for gantt chart.
 * @internal
 */
export const DataTableGanttChartFeature = constructGanttChartFeature();
